import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Home, Plus, FileText, Users, Search, Filter,
  Eye, Edit2, Trash2, Send, CheckCircle, Clock, AlertCircle,
  ArrowRight, Download, MoreVertical, Upload, X, Settings, History, MapPin, Receipt, Package, Archive,
  TrendingUp, TrendingDown, Euro, BarChart3, PieChart, Trophy, RefreshCw, FileWarning, Wrench, Star, Shield
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import ReviewRequestModal from '@/components/ReviewRequestModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function InvoicesPage({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'invoices'); // 'invoices', 'quotes', 'clients', 'documents'
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState(null); // Stats nouvelles vs importées
  const [clientsStats, setClientsStats] = useState(null); // Stats des clients par source
  const [syncing, setSyncing] = useState(false); // État de synchronisation
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('new'); // 'new', 'imported', 'all'
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showArchives, setShowArchives] = useState(false); // Afficher les archives
  const [archiveSection, setArchiveSection] = useState('paid'); // 'paid' ou 'pending'
  
  // Documents techniques
  const [diagnostics, setDiagnostics] = useState([]);
  const [irreparabilities, setIrreParabilities] = useState([]);
  const [sendingDoc, setSendingDoc] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  
  // Modal demande d'avis
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewClient, setReviewClient] = useState(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, [filterSource]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Récupérer les stats des nouvelles vs anciennes factures
      const invoiceStatsRes = await axios.get(`${BACKEND_URL}/api/invoices/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoiceStats(invoiceStatsRes.data);
      
      // Construire l'URL avec le filtre source
      const sourceParam = filterSource !== 'all' ? `&source=${filterSource}` : '';
      
      const [invoicesRes, quotesRes, clientsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/invoices?invoice_type=invoice&limit=100000${sourceParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/invoices?invoice_type=quote&limit=100000${sourceParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/invoices/stats/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);

      setInvoices(invoicesRes.data.items || invoicesRes.data);
      setQuotes(quotesRes.data.items || quotesRes.data);
      setClients(clientsRes.data.items || clientsRes.data);
      setStats(statsRes.data);
      
      // Charger les documents techniques
      try {
        const [diagRes, irrRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/technical-docs/diagnostics`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${BACKEND_URL}/api/technical-docs/irreparability`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setDiagnostics(diagRes.data);
        setIrreParabilities(irrRes.data);
      } catch (e) {
        console.log('No technical docs yet');
      }
      
      // Charger les stats des clients
      try {
        const clientsStatsRes = await axios.get(`${BACKEND_URL}/api/clients/stats/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClientsStats(clientsStatsRes.data);
      } catch (e) {
        console.log('Stats clients non disponibles');
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Fonction de synchronisation des contacts
  const handleSyncContacts = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/clients/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { synced, sources, total_clients, message } = response.data;
      
      if (synced > 0) {
        toast.success(`${synced} contacts synchronisés !`, {
          description: message
        });
      } else {
        toast.info('Tous les contacts sont déjà synchronisés');
      }
      
      // Recharger les données
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: 'Brouillon', class: 'bg-slate-500/20 text-slate-300' },
      sent: { label: 'Envoyée', class: 'bg-blue-500/20 text-blue-300' },
      paid: { label: 'PAYÉ', class: 'bg-emerald-500/30 text-emerald-400 font-bold border border-emerald-500/50' },
      partial: { label: 'Partiel', class: 'bg-amber-500/20 text-amber-300' },
      overdue: { label: 'En retard', class: 'bg-red-500/20 text-red-300' },
      cancelled: { label: 'Annulée', class: 'bg-slate-500/20 text-slate-400' },
      converted: { label: 'Convertie', class: 'bg-purple-500/20 text-purple-300' }
    };
    const badge = badges[status] || badges.draft;
    return (
      <span className={`px-3 py-1 rounded-full text-sm ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  // Fonctions pour les documents techniques
  const handleDeleteDoc = async (docType, docId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;
    
    try {
      const endpoint = docType === 'diagnostic' 
        ? `${BACKEND_URL}/api/technical-docs/diagnostics/${docId}`
        : `${BACKEND_URL}/api/technical-docs/irreparability/${docId}`;
      
      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handlePrintDoc = (docType, docId) => {
    const endpoint = docType === 'diagnostic'
      ? `${BACKEND_URL}/api/technical-docs/diagnostics/${docId}/pdf`
      : `${BACKEND_URL}/api/technical-docs/irreparability/${docId}/pdf`;
    window.open(`${endpoint}?token=${token}`, '_blank');
  };

  const handleSendDocEmail = async (docType, doc) => {
    if (!emailTo) {
      toast.error('Veuillez entrer une adresse email');
      return;
    }
    
    setSendingDoc(doc.id);
    try {
      // Récupérer le PDF et l'envoyer par email via l'API existante
      await axios.post(`${BACKEND_URL}/api/technical-docs/${docType === 'diagnostic' ? 'diagnostics' : 'irreparability'}/${doc.id}/send-email`, {
        email: emailTo,
        subject: docType === 'diagnostic' 
          ? `Rapport de Diagnostic - ${doc.number}`
          : `Attestation d'Irréparabilité - ${doc.number}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email envoyé avec succès !');
      setShowEmailModal(null);
      setEmailTo('');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingDoc(null);
    }
  };

  // Obtenir le premier jour du mois en cours
  const now = new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  
  // Séparer les factures : mois en cours vs archives (seulement si pas de filtre source spécifique)
  const currentMonthInvoices = invoices
    .filter(inv => {
      const invoiceDate = new Date(inv.date || inv.created_at);
      return invoiceDate >= currentMonthStart;
    })
    .sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at)); // Plus récentes en premier
  
  // Archives = factures des mois précédents
  const archivedInvoices = invoices
    .filter(inv => {
      const invoiceDate = new Date(inv.date || inv.created_at);
      return invoiceDate < currentMonthStart;
    })
    .sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at));
  
  // Sous-catégories des archives
  const archivedPaid = archivedInvoices.filter(inv => inv.status === 'paid');
  const archivedPending = archivedInvoices.filter(inv => inv.status !== 'paid');
  
  // Factures affichées : si filterSource est utilisé, afficher toutes les factures reçues de l'API
  // Sinon, utiliser la logique d'archives par date
  const displayedInvoices = invoices.sort((a, b) => 
    new Date(b.date || b.created_at) - new Date(a.date || a.created_at)
  );
  
  // Appliquer les filtres de recherche et statut
  const filteredInvoices = displayedInvoices.filter(inv => {
    const clientName = inv.client_name || '';
    const invoiceNumber = inv.invoice_number || inv.number || '';
    const matchesSearch = clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || inv.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const filteredQuotes = quotes.filter(q => {
    const clientName = q.client_name || '';
    const quoteNumber = q.invoice_number || q.number || '';
    const matchesSearch = clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quoteNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || q.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const filteredClients = clients.filter(c => 
    (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-black" data-testid="invoices-page">
      {/* Header compact */}
      <header className="sticky top-0 z-50 bg-gray-900 border-b border-gray-700 h-14 flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
            data-testid="home-btn"
          >
            <Home className="w-4 h-4 mr-2" />
            Caisse
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-emerald-400 bg-white/10"
          >
            <FileText className="w-4 h-4 mr-2" />
            Facturation
          </Button>
          <Button
            onClick={() => navigate('/deposit-receipts')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <Receipt className="w-4 h-4 mr-2" />
            Bons d'entrée
          </Button>
          <Button
            onClick={() => navigate('/order-alerts')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <Package className="w-4 h-4 mr-2" />
            Commandes
          </Button>
          <Button
            onClick={() => navigate('/history')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <History className="w-4 h-4 mr-2" />
            Historique
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate('/settings/company')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
            data-testid="settings-btn"
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => setShowImportModal(true)}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
            data-testid="import-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importer
          </Button>
          <Button
            onClick={() => navigate('/invoices/new?type=quote')}
            variant="outline"
            size="sm"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="new-quote-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Devis
          </Button>
          <Button
            onClick={() => navigate('/invoices/new?type=invoice')}
            size="sm"
            data-testid="new-invoice-btn"
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Facture
          </Button>
          <Button
            onClick={() => navigate('/invoices/warranty/new')}
            size="sm"
            data-testid="new-warranty-invoice-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Shield className="w-4 h-4 mr-2" />
            Facture + Garantie
          </Button>
          <Button
            onClick={() => navigate('/technical-docs/diagnostic')}
            size="sm"
            data-testid="new-diagnostic-btn"
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
          >
            <Wrench className="w-4 h-4 mr-2" />
            Diagnostic
          </Button>
          <Button
            onClick={() => navigate('/technical-docs/irreparability')}
            size="sm"
            data-testid="new-irreparability-btn"
            className="bg-red-500 hover:bg-red-600 text-white"
          >
            <FileWarning className="w-4 h-4 mr-2" />
            Irréparabilité
          </Button>
          <Button
            onClick={() => navigate('/invoices/warranty/history')}
            size="sm"
            data-testid="warranty-history-btn"
            className="bg-green-500 hover:bg-green-600 text-white"
          >
            <Star className="w-4 h-4 mr-2" />
            Signatures Garantie
          </Button>
        </div>
      </header>

      {/* Main Content - pleine largeur */}
      <main className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
        {/* Stats Cards - Tableau de bord amélioré */}
        {stats && (
          <div className="space-y-4">
            {/* Première ligne - Stats principales */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-slate-400">Total Facturé</div>
                  <Euro className="w-5 h-5 text-slate-500" />
                </div>
                <div className="text-2xl font-bold text-white">{formatCurrency(stats.invoices?.total_invoiced || 0)}</div>
                <div className="text-xs text-slate-500 mt-1">{stats.invoices?.active_count || 0} factures actives</div>
              </div>
              <div className="bg-gradient-to-br from-emerald-900/50 to-emerald-950/50 rounded-xl p-4 border border-emerald-700/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-emerald-400">Encaissé</div>
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="text-2xl font-bold text-emerald-300">{formatCurrency(stats.invoices?.total_paid || 0)}</div>
                <div className="text-xs text-emerald-500/70 mt-1">{stats.invoices?.paid_count || 0} factures payées</div>
              </div>
              <div className="bg-gradient-to-br from-amber-900/50 to-amber-950/50 rounded-xl p-4 border border-amber-700/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-amber-400">En attente</div>
                  <Clock className="w-5 h-5 text-amber-500" />
                </div>
                <div className="text-2xl font-bold text-amber-300">{formatCurrency(stats.invoices?.total_pending || 0)}</div>
                <div className="text-xs text-amber-500/70 mt-1">{stats.invoices?.pending_count || 0} factures</div>
              </div>
              <div className="bg-gradient-to-br from-red-900/50 to-red-950/50 rounded-xl p-4 border border-red-700/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-red-400">En retard</div>
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
                <div className="text-2xl font-bold text-red-300">{formatCurrency(stats.invoices?.total_overdue || 0)}</div>
                <div className="text-xs text-red-500/70 mt-1">{stats.invoices?.overdue_count || 0} factures</div>
              </div>
            </div>
            
            {/* Deuxième ligne - Stats mensuelles et devis */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Performance mensuelle */}
              <div className="bg-slate-900 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                  <span className="text-sm font-medium text-white">Ce mois</span>
                  {stats.monthly?.variation_percent !== undefined && (
                    <span className={`ml-auto flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                      stats.monthly.variation_percent >= 0 
                        ? 'bg-emerald-500/20 text-emerald-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {stats.monthly.variation_percent >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {stats.monthly.variation_percent >= 0 ? '+' : ''}{stats.monthly.variation_percent}%
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">CA facturé</span>
                    <span className="text-white font-medium">{formatCurrency(stats.monthly?.current_month_total || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">CA encaissé</span>
                    <span className="text-emerald-400 font-medium">{formatCurrency(stats.monthly?.current_month_paid || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Mois précédent</span>
                    <span className="text-slate-500 text-sm">{formatCurrency(stats.monthly?.previous_month_total || 0)}</span>
                  </div>
                </div>
              </div>
              
              {/* Devis */}
              <div className="bg-slate-900 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center gap-2 mb-3">
                  <PieChart className="w-5 h-5 text-purple-400" />
                  <span className="text-sm font-medium text-white">Devis</span>
                  {stats.quotes?.conversion_rate > 0 && (
                    <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                      {stats.quotes.conversion_rate.toFixed(0)}% convertis
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Total devisé</span>
                    <span className="text-white font-medium">{formatCurrency(stats.quotes?.total_quoted || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Convertis en facture</span>
                    <span className="text-purple-400 font-medium">{formatCurrency(stats.quotes?.total_converted || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">En attente</span>
                    <span className="text-slate-500 text-sm">{stats.quotes?.pending_count || 0} devis</span>
                  </div>
                </div>
              </div>
              
              {/* Top Clients */}
              <div className="bg-slate-900 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                  <span className="text-sm font-medium text-white">Top Clients</span>
                </div>
                <div className="space-y-2">
                  {stats.top_clients && stats.top_clients.length > 0 ? (
                    stats.top_clients.slice(0, 4).map((client, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-slate-400 text-sm truncate max-w-[140px]" title={client.name}>
                          {idx + 1}. {client.name}
                        </span>
                        <span className="text-white font-medium text-sm">{formatCurrency(client.total)}</span>
                      </div>
                    ))
                  ) : (
                    <div className="text-slate-500 text-sm text-center py-2">Aucun client</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden">
          <div className="flex border-b border-gray-700">
            <button
              onClick={() => setActiveTab('invoices')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'invoices' 
                  ? 'text-white border-b-2 border-blue-400 bg-gray-800' 
                  : 'text-white/60 hover:text-white'
              }`}
              data-testid="tab-invoices"
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Factures ({invoices.length})
            </button>
            <button
              onClick={() => setActiveTab('quotes')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'quotes' 
                  ? 'text-white border-b-2 border-blue-400 bg-gray-800' 
                  : 'text-white/60 hover:text-white'
              }`}
              data-testid="tab-quotes"
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Devis ({quotes.length})
            </button>
            <button
              onClick={() => setActiveTab('clients')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'clients' 
                  ? 'text-white border-b-2 border-blue-400 bg-gray-800' 
                  : 'text-white/60 hover:text-white'
              }`}
              data-testid="tab-clients"
            >
              <Users className="w-4 h-4 inline mr-2" />
              Clients ({clients.length})
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'documents' 
                  ? 'text-white border-b-2 border-cyan-400 bg-gray-800' 
                  : 'text-white/60 hover:text-white'
              }`}
              data-testid="tab-documents"
            >
              <Wrench className="w-4 h-4 inline mr-2" />
              Documents ({diagnostics.length + irreparabilities.length})
            </button>
          </div>

          {/* Search & Filter */}
          <div className="p-4 border-b border-gray-700 flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-gray-800 border-gray-600 text-white placeholder-white/40"
              />
            </div>
            {activeTab !== 'clients' && (
              <>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-sm text-white"
                >
                  <option value="all" className="bg-gray-800">Tous les statuts</option>
                  <option value="draft" className="bg-gray-800">Brouillons</option>
                  <option value="sent" className="bg-slate-800">Envoyées</option>
                  <option value="paid" className="bg-slate-800">Payées</option>
                  <option value="overdue" className="bg-slate-800">En retard</option>
                </select>
                {activeTab === 'invoices' && (
                  <Button 
                    onClick={() => { setShowArchives(!showArchives); setFilterStatus('all'); }}
                    variant={showArchives ? "default" : "outline"}
                    size="sm"
                    className={showArchives 
                      ? "bg-slate-600 hover:bg-slate-500" 
                      : "border-white/20 text-white hover:bg-white/10"
                    }
                  >
                    <Archive className="w-4 h-4 mr-2" />
                    Archives ({archivedInvoices.length})
                  </Button>
                )}
              </>
            )}
            {activeTab === 'clients' && (
              <Button onClick={() => navigate('/clients/new')} variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Client
              </Button>
            )}
          </div>

          {/* Content */}
          <div className="p-4">
            {loading ? (
              <div className="text-center py-12 text-white/60">Chargement...</div>
            ) : (
              <>
                {/* Invoices Tab */}
                {activeTab === 'invoices' && (
                  <div className="space-y-2">
                    {/* Titre section */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h2 className="text-lg font-semibold text-white">
                            {filterSource === 'new' 
                              ? `Mes Factures (${invoiceStats?.new_invoices || 0})`
                              : filterSource === 'imported'
                              ? `Archives Tiime (${invoiceStats?.imported_invoices || 0})`
                              : `Toutes les Factures (${invoiceStats?.total_invoices || 0})`
                            }
                          </h2>
                          <p className="text-white/50 text-sm">
                            {filterSource === 'new' 
                              ? 'Factures créées dans cette application'
                              : filterSource === 'imported'
                              ? 'Factures importées depuis Tiime (archive)'
                              : 'Nouvelles + Archives Tiime'
                            }
                          </p>
                        </div>
                      </div>
                      
                      {/* Boutons de filtre Nouvelles / Archives */}
                      <div className="flex gap-2 mt-3">
                        <Button
                          onClick={() => setFilterSource('new')}
                          size="sm"
                          className={filterSource === 'new' 
                            ? "bg-emerald-600 hover:bg-emerald-500" 
                            : "bg-white/10 hover:bg-white/20 text-white"
                          }
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Mes Factures ({invoiceStats?.new_invoices || 0})
                        </Button>
                        <Button
                          onClick={() => setFilterSource('imported')}
                          size="sm"
                          className={filterSource === 'imported' 
                            ? "bg-amber-600 hover:bg-amber-500" 
                            : "bg-white/10 hover:bg-white/20 text-white"
                          }
                        >
                          <Archive className="w-4 h-4 mr-2" />
                          Archives Tiime ({invoiceStats?.imported_invoices || 0})
                        </Button>
                        <Button
                          onClick={() => setFilterSource('all')}
                          size="sm"
                          className={filterSource === 'all' 
                            ? "bg-blue-600 hover:bg-blue-500" 
                            : "bg-white/10 hover:bg-white/20 text-white"
                          }
                        >
                          Tout ({invoiceStats?.total_invoices || 0})
                        </Button>
                      </div>
                    </div>
                    
                    {filteredInvoices.length === 0 ? (
                      <div className="text-center py-12 text-white/60">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-white/30" />
                        <p>Aucune facture</p>
                        <Button onClick={() => navigate('/invoices/new?type=invoice')} className="mt-4 bg-blue-500 hover:bg-blue-600">
                          Créer une facture
                        </Button>
                      </div>
                    ) : (
                      filteredInvoices.map((inv) => (
                        <div
                          key={inv.id}
                          className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer border border-white/10"
                          onClick={() => navigate(`/invoices/${inv.id}`)}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${
                              inv.is_warranty_invoice 
                                ? 'bg-emerald-500/20 border-emerald-500/30' 
                                : 'bg-white/10 border-white/20'
                            }`}>
                              {inv.is_warranty_invoice ? (
                                <Shield className="w-5 h-5 text-emerald-400" />
                              ) : (
                                <FileText className="w-5 h-5 text-white/80" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-white">{inv.invoice_number || inv.number || '—'}</span>
                                {inv.is_warranty_invoice && (
                                  <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 rounded-full text-xs font-medium border border-emerald-500/30">
                                    GARANTI
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-white/60">{inv.client_name}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <div className="font-medium text-white">{formatCurrency(inv.total_ttc)}</div>
                              <div className="text-xs text-white/50">{inv.issue_date}</div>
                            </div>
                            {getStatusBadge(inv.status)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Quotes Tab */}
                {activeTab === 'quotes' && (
                  <div className="space-y-2">
                    {filteredQuotes.length === 0 ? (
                      <div className="text-center py-12 text-white/60">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-white/30" />
                        <p>Aucun devis</p>
                        <Button onClick={() => navigate('/invoices/new?type=quote')} className="mt-4 bg-blue-500 hover:bg-blue-600">
                          Créer un devis
                        </Button>
                      </div>
                    ) : (
                      filteredQuotes.map((q) => (
                        <div
                          key={q.id}
                          className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer border border-white/10"
                          onClick={() => navigate(`/invoices/${q.id}`)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center border border-blue-500/30">
                              <FileText className="w-5 h-5 text-blue-300" />
                            </div>
                            <div>
                              <div className="font-medium text-white">{q.invoice_number || q.number || '—'}</div>
                              <div className="text-sm text-white/60">{q.client_name}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <div className="font-medium text-white">{formatCurrency(q.total_ttc)}</div>
                              <div className="text-xs text-white/50">{q.issue_date}</div>
                            </div>
                            {getStatusBadge(q.status)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Clients Tab */}
                {activeTab === 'clients' && (
                  <div className="space-y-4">
                    {/* En-tête avec stats et bouton sync */}
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-lg font-semibold text-white">
                          Contacts ({clients.length})
                        </h2>
                        {clientsStats && (
                          <p className="text-white/50 text-sm">
                            {clientsStats.imported_tiime > 0 && `${clientsStats.imported_tiime} Tiime • `}
                            {clientsStats.from_deposits > 0 && `${clientsStats.from_deposits} Bons d'entrée • `}
                            {clientsStats.from_orders > 0 && `${clientsStats.from_orders} Commandes • `}
                            {clientsStats.from_invoices > 0 && `${clientsStats.from_invoices} Factures`}
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={handleSyncContacts}
                        disabled={syncing}
                        className="bg-emerald-600 hover:bg-emerald-500"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                        {syncing ? 'Synchronisation...' : 'Synchroniser contacts'}
                      </Button>
                    </div>
                    
                    {/* Liste des clients */}
                    {filteredClients.length === 0 ? (
                      <div className="text-center py-12 text-white/60">
                        <Users className="w-12 h-12 mx-auto mb-4 text-white/30" />
                        <p>Aucun client</p>
                        <Button onClick={() => navigate('/clients/new')} className="mt-4 bg-blue-500 hover:bg-blue-600">
                          Ajouter un client
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredClients.map((client) => (
                          <div
                            key={client.id}
                            className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors border border-white/10"
                          >
                            <div className="flex items-center gap-4 flex-1 cursor-pointer" onClick={() => navigate(`/clients/${client.id}`)}>
                              <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center border border-white/20 text-white font-medium">
                                {(client.name || '?').charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-white">{client.name}</div>
                                <div className="text-sm text-white/60 flex flex-wrap gap-x-3">
                                  {client.phone && <span>{client.phone}</span>}
                                  {client.email && <span>{client.email}</span>}
                                  {!client.phone && !client.email && <span>Pas de contact</span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {client.source && (
                                <span className={`px-2 py-0.5 rounded-full text-xs ${
                                  client.source === 'deposit' || client.source === 'deposit_receipt' ? 'bg-purple-500/20 text-purple-300' :
                                  client.source === 'order' || client.source === 'order_alert' ? 'bg-orange-500/20 text-orange-300' :
                                  client.source === 'invoice' || client.source === 'invoice_sync' ? 'bg-blue-500/20 text-blue-300' :
                                  'bg-white/10 text-white/50'
                                }`}>
                                  {client.source === 'deposit' || client.source === 'deposit_receipt' ? 'Bon' :
                                   client.source === 'order' || client.source === 'order_alert' ? 'Commande' :
                                   client.source === 'invoice' || client.source === 'invoice_sync' ? 'Facture' : ''}
                                </span>
                              )}
                              {client.client_type === 'professionnel' ? (
                                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded-full text-xs">Pro</span>
                              ) : (
                                <span className="px-2 py-0.5 bg-white/10 text-white/60 rounded-full text-xs">Particulier</span>
                              )}
                              {/* Bouton Demander un avis */}
                              {(client.phone || client.email) && (
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setReviewClient(client);
                                    setShowReviewModal(true);
                                  }}
                                  variant="ghost"
                                  size="sm"
                                  className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10"
                                  title="Demander un avis"
                                >
                                  <Star className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/clients/${client.id}`);
                                }}
                                variant="ghost"
                                size="sm"
                                className="text-white/60 hover:text-white hover:bg-white/10"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <div className="p-6">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Documents Techniques</h3>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => navigate('/technical-docs/diagnostic')}
                        size="sm"
                        className="bg-cyan-500 hover:bg-cyan-600 text-white"
                      >
                        <Wrench className="w-4 h-4 mr-2" />
                        Nouveau Diagnostic
                      </Button>
                      <Button
                        onClick={() => navigate('/technical-docs/irreparability')}
                        size="sm"
                        className="bg-red-500 hover:bg-red-600 text-white"
                      >
                        <FileWarning className="w-4 h-4 mr-2" />
                        Nouvelle Attestation
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Rapports de Diagnostic */}
                <div className="mb-8">
                  <h4 className="text-md font-medium text-cyan-400 mb-3 flex items-center gap-2">
                    <Wrench className="w-5 h-5" />
                    Rapports de Diagnostic ({diagnostics.length})
                  </h4>
                  {diagnostics.length === 0 ? (
                    <div className="text-center py-8 text-white/40">
                      <Wrench className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Aucun rapport de diagnostic</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {diagnostics.map((doc) => (
                        <div key={doc.id} className="bg-gray-800/50 rounded-lg border border-gray-700 p-4 hover:border-cyan-500/50 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-cyan-500/20 rounded-full flex items-center justify-center">
                                <Wrench className="w-5 h-5 text-cyan-400" />
                              </div>
                              <div>
                                <div className="font-medium text-white flex items-center gap-2">
                                  {doc.number}
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    doc.status === 'finalized' 
                                      ? 'bg-emerald-500/20 text-emerald-400' 
                                      : 'bg-amber-500/20 text-amber-400'
                                  }`}>
                                    {doc.status === 'finalized' ? 'Finalisé' : 'Brouillon'}
                                  </span>
                                  {doc.use_ai_version && (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                                      IA
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-white/60">
                                  {doc.client_name} - {doc.device_brand} {doc.device_model}
                                </div>
                                <div className="text-xs text-white/40">
                                  {doc.created_at ? new Date(doc.created_at).toLocaleDateString('fr-FR') : ''}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handlePrintDoc('diagnostic', doc.id)}
                                className="text-white/60 hover:text-white h-8 w-8 p-0"
                                title="Imprimer / Télécharger PDF"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setShowEmailModal({ type: 'diagnostic', doc }); setEmailTo(doc.client_email || ''); }}
                                className="text-blue-400 hover:text-blue-300 h-8 w-8 p-0"
                                title="Envoyer par email"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/technical-docs/diagnostic/${doc.id}`)}
                                className="text-white/60 hover:text-white h-8 w-8 p-0"
                                title="Modifier"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteDoc('diagnostic', doc.id)}
                                className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Attestations d'Irréparabilité */}
                <div>
                  <h4 className="text-md font-medium text-red-400 mb-3 flex items-center gap-2">
                    <FileWarning className="w-5 h-5" />
                    Attestations d'Irréparabilité ({irreparabilities.length})
                  </h4>
                  {irreparabilities.length === 0 ? (
                    <div className="text-center py-8 text-white/40">
                      <FileWarning className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Aucune attestation d'irréparabilité</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {irreparabilities.map((doc) => (
                        <div key={doc.id} className="bg-gray-800/50 rounded-lg border border-gray-700 p-4 hover:border-red-500/50 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                                <FileWarning className="w-5 h-5 text-red-400" />
                              </div>
                              <div>
                                <div className="font-medium text-white flex items-center gap-2">
                                  {doc.number}
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    doc.status === 'finalized' 
                                      ? 'bg-emerald-500/20 text-emerald-400' 
                                      : 'bg-amber-500/20 text-amber-400'
                                  }`}>
                                    {doc.status === 'finalized' ? 'Finalisé' : 'Brouillon'}
                                  </span>
                                  {doc.use_ai_version && (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                                      IA
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-white/60">
                                  {doc.client_name} - {doc.device_brand} {doc.device_model}
                                </div>
                                <div className="text-xs text-white/40">
                                  {doc.created_at ? new Date(doc.created_at).toLocaleDateString('fr-FR') : ''}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handlePrintDoc('irreparability', doc.id)}
                                className="text-white/60 hover:text-white h-8 w-8 p-0"
                                title="Imprimer / Télécharger PDF"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setShowEmailModal({ type: 'irreparability', doc }); setEmailTo(doc.client_email || ''); }}
                                className="text-blue-400 hover:text-blue-300 h-8 w-8 p-0"
                                title="Envoyer par email"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/technical-docs/irreparability/${doc.id}`)}
                                className="text-white/60 hover:text-white h-8 w-8 p-0"
                                title="Modifier"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteDoc('irreparability', doc.id)}
                                className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Email Modal for Documents */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6 border border-gray-600">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                Envoyer par email
              </h2>
              <button onClick={() => { setShowEmailModal(null); setEmailTo(''); }} className="text-white/40 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-white/60 mb-4">
                Envoyer {showEmailModal.type === 'diagnostic' ? 'le rapport de diagnostic' : 'l\'attestation d\'irréparabilité'} 
                <strong className="text-white"> {showEmailModal.doc.number}</strong> au client.
              </p>
              
              <label className="block text-sm font-medium text-white/80 mb-1">Adresse email</label>
              <Input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="client@email.com"
                className="bg-gray-700 border-gray-600 text-white placeholder-white/40"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => { setShowEmailModal(null); setEmailTo(''); }}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Annuler
              </Button>
              <Button
                onClick={() => handleSendDocEmail(showEmailModal.type, showEmailModal.doc)}
                disabled={sendingDoc === showEmailModal.doc.id || !emailTo}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                {sendingDoc === showEmailModal.doc.id ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Envoi...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Envoyer</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6 border border-gray-600">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Importer depuis Tiime</h2>
              <button onClick={() => setShowImportModal(false)} className="text-white/40 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-white/60 mb-6">
              Importez vos données depuis Tiime en fournissant les URLs des fichiers Excel exportés.
              L'import créera automatiquement les clients manquants.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">URL du fichier Clients (.xlsx)</label>
                <Input 
                  id="import-clients-url"
                  placeholder="https://..." 
                  data-testid="import-clients-url"
                  className="bg-gray-700 border-gray-600 text-white placeholder-white/40"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">URL du fichier Factures (.xlsx)</label>
                <Input 
                  id="import-invoices-url"
                  placeholder="https://..." 
                  data-testid="import-invoices-url"
                  className="bg-gray-700 border-gray-600 text-white placeholder-white/40"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">URL du fichier Devis (.xlsx)</label>
                <Input 
                  id="import-quotes-url"
                  placeholder="https://..." 
                  data-testid="import-quotes-url"
                  className="bg-gray-700 border-gray-600 text-white placeholder-white/40"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowImportModal(false)} className="flex-1 border-white/20 text-white hover:bg-white/10">
                Annuler
              </Button>
              <Button 
                onClick={async () => {
                  setImporting(true);
                  const clientsUrl = document.getElementById('import-clients-url').value;
                  const invoicesUrl = document.getElementById('import-invoices-url').value;
                  const quotesUrl = document.getElementById('import-quotes-url').value;
                  
                  try {
                    let totalImported = 0;
                    
                    if (clientsUrl) {
                      const res = await axios.post(
                        `${BACKEND_URL}/api/import/clients?file_url=${encodeURIComponent(clientsUrl)}`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      totalImported += res.data.imported_count;
                      toast.success(`${res.data.imported_count} clients importés`);
                    }
                    
                    if (invoicesUrl) {
                      const res = await axios.post(
                        `${BACKEND_URL}/api/import/invoices?file_url=${encodeURIComponent(invoicesUrl)}&invoice_type=invoice`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      totalImported += res.data.imported_count;
                      toast.success(`${res.data.imported_count} factures importées`);
                    }
                    
                    if (quotesUrl) {
                      const res = await axios.post(
                        `${BACKEND_URL}/api/import/invoices?file_url=${encodeURIComponent(quotesUrl)}&invoice_type=quote`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      totalImported += res.data.imported_count;
                      toast.success(`${res.data.imported_count} devis importés`);
                    }
                    
                    if (totalImported > 0) {
                      fetchData();
                    }
                    setShowImportModal(false);
                  } catch (error) {
                    toast.error('Erreur lors de l\'import: ' + (error.response?.data?.detail || error.message));
                  } finally {
                    setImporting(false);
                  }
                }}
                className="flex-1 bg-blue-500 hover:bg-blue-600"
                disabled={importing}
                data-testid="import-submit-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {importing ? 'Import...' : 'Importer'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal demande d'avis */}
      <ReviewRequestModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setReviewClient(null);
        }}
        client={reviewClient}
        token={token}
        onSuccess={() => {
          setShowReviewModal(false);
          setReviewClient(null);
          toast.success('Demande d\'avis envoyée !');
        }}
      />
    </div>
  );
}
